// ─── Core domain types ──────────────────────────────────────────────────────

export type Priority = 'high' | 'medium' | 'low' | 'none';
export type SessionType = 'work' | 'shortBreak' | 'longBreak';
export type TimerStatus = 'idle' | 'running' | 'paused' | 'finished';
export type RecurrenceRule = 'none' | 'daily' | 'weekly' | 'weekdays' | string; // "every Monday", etc.

export interface Project {
  id: string;
  uid: string;
  name: string;
  color: string; // hex
  folderId?: string;
  createdAt: number;
  updatedAt: number;
  archived: boolean;
}

export interface ProjectFolder {
  id: string;
  uid: string;
  name: string;
  createdAt: number;
}

export interface Subtask {
  id: string;
  name: string;
  completed: boolean;
  estimatedPomodoros: number;
  completedPomodoros: number;
}

export interface Task {
  id: string;
  uid: string;
  projectId?: string;
  name: string;
  notes?: string;
  priority: Priority;
  tags: string[];
  dueDate?: number;    // unix ms
  reminder?: number;   // unix ms
  recurrence: RecurrenceRule;
  estimatedPomodoros: number;
  completedPomodoros: number;
  subtasks: Subtask[];
  completed: boolean;
  completedAt?: number;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export interface Session {
  id: string;
  uid: string;
  taskId?: string;
  projectId?: string;
  type: SessionType;
  startedAt: number;
  endedAt?: number;
  durationSeconds: number;
  completed: boolean;
}

export interface ForestTree {
  id: string;
  uid: string;
  sessionId: string;
  taskId?: string;
  projectId?: string;
  grownAt: number;
  species: number; // 0-4 tree variants
  size: number;    // 1-5 growth stage
}

export interface Settings {
  workDuration: number;       // minutes (default 25)
  shortBreakDuration: number; // minutes (default 5)
  longBreakDuration: number;  // minutes (default 15)
  longBreakAfter: number;     // pomodoros before long break (default 4)
  autoStartBreaks: boolean;
  autoStartPomodoros: boolean;
  notifyMinutesBefore: number;
  alarmSound: string;
  alarmVolume: number;        // 0-1
  tickingEnabled: boolean;    // clock tick during focus
  tickingVolume: number;      // 0-1
  strictMode: boolean;        // block pause/exit during focus
  dailyGoal: number;          // target pomodoros per day
  darkMode: 'light' | 'dark' | 'system';
  language: 'es' | 'en';
  countingUp: boolean;        // countdown vs count-up mode
  whiteNoise: WhiteNoiseSettings;
}

export interface WhiteNoiseSettings {
  enabled: boolean;
  sounds: { [key: string]: number }; // sound id → volume 0-1
}

export interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  totalFocusSeconds: number;
  totalSessions: number;
  currentStreak: number;
  longestStreak: number;
  lastSessionDate?: string; // YYYY-MM-DD
  xp: number;
}

// ─── Timer state ────────────────────────────────────────────────────────────

export interface TimerState {
  status: TimerStatus;
  sessionType: SessionType;
  taskId?: string;
  startedAt?: number;       // unix ms when current segment started
  elapsed: number;          // seconds elapsed in current segment
  duration: number;         // total seconds for current session
  pomodorosThisSet: number; // how many work sessions done in current set
  isFocusMode: boolean;
}

// ─── Cloud timer (cross-device sync) ─────────────────────────────────────────

export interface CloudTimer {
  status: 'idle' | 'running' | 'paused' | 'finished';
  sessionType: SessionType;
  taskId?: string;
  duration: number;          // total seconds
  remaining: number;         // seconds left (authoritative when not running)
  endsAt?: number;           // absolute ms the session ends (while running)
  pomodorosThisSet: number;
  sessionStartedAt?: number; // ms when the current work session first started
  updatedAt: number;
}

// ─── MAXER (fitness app) state ────────────────────────────────────────────────
// Stored at users/{uid}/data/state in the unified Firebase. MAXER owns the full
// shape; FlowFocus reads it read-only for the "Tu día" hub, so this is loose and
// only names the fields we actually surface.
export interface MaxerState {
  streak?: number;
  xp?: number;
  xpToday?: number;
  rehabWeek?: number;
  rehabDaysThisWeek?: number;
  habits?: Record<string, boolean>;
  pushups?: Record<string, unknown[]>;
  legs?: Record<string, unknown[]>;
  general?: Record<string, unknown[]>;
  profile?: { name?: string; rehabDaysGoal?: number };
  recomp?: { goal?: { targetWeight?: number; targetBF?: number; startWeight?: number; startBF?: number } };
  [key: string]: unknown;
}

// ─── Smart view ids ─────────────────────────────────────────────────────────

export type SmartViewId = 'today' | 'tomorrow' | 'thisWeek' | 'planned' | 'completed' | 'overdue' | 'highPriority' | 'all';

// ─── Level system ────────────────────────────────────────────────────────────

export interface Level {
  level: number;
  name: string;
  minHours: number;
  maxHours: number;
  icon: string;
}

export const LEVELS: Level[] = [
  { level: 1, name: 'Semilla',       minHours: 0,   maxHours: 5,   icon: '🌱' },
  { level: 2, name: 'Brote',         minHours: 5,   maxHours: 15,  icon: '🌿' },
  { level: 3, name: 'Arbusto',       minHours: 15,  maxHours: 30,  icon: '🌾' },
  { level: 4, name: 'Árbol',         minHours: 30,  maxHours: 60,  icon: '🌳' },
  { level: 5, name: 'Bosque',        minHours: 60,  maxHours: 120, icon: '🌲' },
  { level: 6, name: 'Bosque Antiguo',minHours: 120, maxHours: Infinity, icon: '🌳' },
];

export function getLevelFromHours(hours: number): Level {
  return [...LEVELS].reverse().find(l => hours >= l.minHours) ?? LEVELS[0];
}

// ─── White noise sounds ──────────────────────────────────────────────────────

export interface WhiteNoiseSound {
  id: string;
  label: string;
  labelEn: string;
  emoji: string;
  url: string; // CC0 audio URL
}

export const WHITE_NOISE_SOUNDS: WhiteNoiseSound[] = [
  { id: 'rain',    label: 'Lluvia',      labelEn: 'Rain',        emoji: '🌧️', url: 'https://assets.mixkit.co/sfx/preview/mixkit-light-rain-loop-1253.mp3' },
  { id: 'cafe',    label: 'Cafetería',   labelEn: 'Café',        emoji: '☕', url: 'https://assets.mixkit.co/sfx/preview/mixkit-coffee-shop-ambience-443.mp3' },
  { id: 'forest',  label: 'Bosque',      labelEn: 'Forest',      emoji: '🌲', url: 'https://assets.mixkit.co/sfx/preview/mixkit-forest-birds-ambience-1210.mp3' },
  { id: 'ocean',   label: 'Océano',      labelEn: 'Ocean',       emoji: '🌊', url: 'https://assets.mixkit.co/sfx/preview/mixkit-ocean-waves-loop-1196.mp3' },
  { id: 'fire',    label: 'Fuego',       labelEn: 'Fire',        emoji: '🔥', url: 'https://assets.mixkit.co/sfx/preview/mixkit-campfire-crackles-1330.mp3' },
  { id: 'fan',     label: 'Ventilador',  labelEn: 'Fan',         emoji: '💨', url: 'https://assets.mixkit.co/sfx/preview/mixkit-air-vent-spinning-3160.mp3' },
  { id: 'brown',   label: 'Brown Noise', labelEn: 'Brown Noise', emoji: '🎵', url: 'https://assets.mixkit.co/sfx/preview/mixkit-quiet-forest-ambience-522.mp3' },
  { id: 'pink',    label: 'Pink Noise',  labelEn: 'Pink Noise',  emoji: '🎶', url: 'https://assets.mixkit.co/sfx/preview/mixkit-soft-rain-loop-2393.mp3' },
];

// ─── Priority helpers ────────────────────────────────────────────────────────

export const PRIORITY_COLORS: Record<Priority, string> = {
  high:   '#FF6B6B',
  medium: '#EF9F27',
  low:    '#378ADD',
  none:   '#AEAEB2',
};

export const PRIORITY_LABELS: Record<Priority, { es: string; en: string }> = {
  high:   { es: 'Alta',  en: 'High'   },
  medium: { es: 'Media', en: 'Medium' },
  low:    { es: 'Baja',  en: 'Low'    },
  none:   { es: 'Sin',   en: 'None'   },
};

// ─── Default settings ────────────────────────────────────────────────────────

export const DEFAULT_SETTINGS: Settings = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  longBreakAfter: 4,
  autoStartBreaks: false,
  autoStartPomodoros: false,
  notifyMinutesBefore: 1,
  alarmSound: 'bell',
  alarmVolume: 0.8,
  tickingEnabled: false,
  tickingVolume: 0.25,
  strictMode: false,
  dailyGoal: 8,
  darkMode: 'system',
  language: 'es',
  countingUp: false,
  whiteNoise: { enabled: false, sounds: {} },
};
